import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ServiceordreAfregningPostDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-afregning-post-dto';
import { BehaviorSubject } from 'rxjs';
import { BetalerService } from 'src/app/api/services';
import { BetalerDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/betaler-dto';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { dkCurrencyPipe } from 'src/app/pipes/dk-currency';

@Component({
    selector: 'app-posterings-list',
    templateUrl: './posterings-list.component.html',
    styleUrls: ['./posterings-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PosteringsListComponent implements OnInit {
  betalere: BetalerDto[];

  constructor(private betalerService: BetalerService, private dkCurrencyPipe: dkCurrencyPipe) {}

  activeBetaler: number;

  onlyPartnerVareNr: boolean = false;

  _posteringsLinjer: ServiceordreAfregningPostDto[];
  _filteredPosteringsLinjer: ServiceordreAfregningPostDto[];
  get posteringsLinjer(): ServiceordreAfregningPostDto[] {
    return this._posteringsLinjer;
  }

  @Input() set posteringsLinjer(value: ServiceordreAfregningPostDto[]) {
    this._posteringsLinjer = value;
    this.onlyPartnerVareNr = value.every((x) => x.okKundeVarenr == null);
    this.updateTable();
    this, (this._filteredPosteringsLinjer = this._posteringsLinjer);
  }
  @Input() editable: boolean = false;
  @Input() isErhverv: boolean = false;

  _isExpanded: boolean = false;
  @Input() set isExpanded(value: boolean) {
    this._isExpanded = value;
    this.activeColumns = this._isExpanded ? this.activeExpandedColumns : this.activeColumnsCollapsed;
    this.headerColumns = this._isExpanded ? this.headerColumnsExpanded : this.headerColumnsCollapsed;
  }
  @Output() linjeEdited: EventEmitter<ServiceordreAfregningPostDto> = new EventEmitter();
  @Output() linjeDeleted: EventEmitter<ServiceordreAfregningPostDto> = new EventEmitter();
  @Output() linierChanged: EventEmitter<ServiceordreAfregningPostDto[]> = new EventEmitter();

  headerColumns: string[];
  headerColumnsExpanded: string[] = ['Varenr', 'placeholder', 'Enhedspris', 'Totalpris'];
  headerColumnsCollapsed: string[] = ['placeholder', 'Enhedspris'];

  activeColumns: string[];
  activeColumnsCollapsed: string[] = ['partnerMatch', 'okKundeVarenr', 'produkt', 'antal', 'stkPrisExclMoms', 'stkPrisInclMoms', 'rowMenu'];
  activeExpandedColumns: string[] = [
    'partnerVareNr',
    'partnerMatch',
    'okKundeVarenr',
    'produkt',
    'antal',
    'stkPrisExclMoms',
    'stkPrisInclMoms',
    'totalPrisExclMoms',
    'totalPrisInclMoms',
    'rowMenu-expanded',
  ];
  activeRabatColumns: string[] = ['N/A', 'rabat-okKundeVarenr', 'rabat-produkt', 'rabat-excl-moms', 'rabat-inkl-moms', 'rabat-row-menu'];
  dataSource = new BehaviorSubject<ServiceordreAfregningPostDto[]>([]);
  saldo: number;
  saldoInclMoms: number;

  ngOnInit(): void {
    this.activeColumns = this._isExpanded ? this.activeExpandedColumns : this.activeColumnsCollapsed;
    this.headerColumns = this._isExpanded ? this.headerColumnsExpanded : this.headerColumnsCollapsed;
    this.betalerService.getBetalerList({ caller: EGIS_SERVICE_CALLER }).subscribe((betalere: BetalerDto[]) => {
      this.betalere = betalere;
    });
  }

  updateSaldo(afregninger: ServiceordreAfregningPostDto[], inclMoms: boolean = false): number {
    return afregninger?.map((x) => this.getPrice(x, inclMoms)).reduce((sum, current) => sum + current, 0);
  }

  getPrice(afregning: ServiceordreAfregningPostDto, inclMoms: boolean): number {
    const pris = (inclMoms ? afregning.stkPrisInclMoms : afregning.stkPrisExclMoms) * afregning.antal;

    if (afregning.rabat == null) {
      return pris;
    }

    const rabatBelob = (inclMoms ? afregning.rabat.rabatInclMoms : afregning.rabat.rabatExclMoms) * afregning.antal;

    if (rabatBelob == null) {
      return pris;
    }

    return pris - rabatBelob;
  }

  updateTable() {
    const filteredPosteringsLinjer =
      this.activeBetaler === undefined ? this.posteringsLinjer : this.posteringsLinjer.filter((x) => x.betaler?.id === this.activeBetaler);

    this.dataSource.next(filteredPosteringsLinjer);
    this.saldo = this.updateSaldo(filteredPosteringsLinjer);
    this.saldoInclMoms = this.updateSaldo(filteredPosteringsLinjer, true);
  }

  garanti(p: ServiceordreAfregningPostDto, betaler: BetalerDto): void {
    p.betaler = betaler;
    this.linierChanged.emit(this.posteringsLinjer);
  }

  posteringsCount(betalerId: number): number {
    return this._posteringsLinjer.filter((x) => x.betaler?.id === betalerId).length;
  }

  filtrer(id?: number | null): void {
    this.activeBetaler = id;
    this.updateTable();
  }

  afregningHasRabat(afregning: ServiceordreAfregningPostDto): boolean {
    return afregning.rabat != null;
  }

  hasRabat = (index: number, item: ServiceordreAfregningPostDto) => item.rabat != null;

  copyPosteringsLinje(linje: ServiceordreAfregningPostDto) {
    return (
      this.formatPosteringsLinje(linje.okKundeVarenr) +
      this.formatPosteringsLinje(linje.produkt) +
      this.formatPosteringsLinje(linje.antal) +
      this.formatPosteringsLinje(this.dkCurrencyPipe.transform(linje.stkPrisExclMoms)) +
      this.formatPosteringsLinje(this.dkCurrencyPipe.transform(linje.stkPrisInclMoms))
    );
  }

  copyPosteringsLinjeExpanded(linje: ServiceordreAfregningPostDto) {
    return (
      this.formatPosteringsLinje(linje.partnerVarenr) +
      this.formatPosteringsLinje(linje.okKundeVarenr) +
      this.formatPosteringsLinje(linje.produkt) +
      this.formatPosteringsLinje(linje.antal) +
      this.formatPosteringsLinje(this.dkCurrencyPipe.transform(linje.stkPrisExclMoms)) +
      this.formatPosteringsLinje(this.dkCurrencyPipe.transform(linje.stkPrisInclMoms)) +
      this.formatPosteringsLinje(this.dkCurrencyPipe.transform(linje.totalPrisExclMoms)) +
      this.formatPosteringsLinje(this.dkCurrencyPipe.transform(linje.totalPrisInclMoms))
    );
  }

  formatPosteringsLinje(value: any): string {
    return (value !== null ? value.toString() : '') + '\t';
  }

  getToolTip(tipType: string, postingLine: ServiceordreAfregningPostDto): string {
    var toolTekst = '';
    switch (tipType) {
      case 'unitPriceNet': {
        toolTekst =
          postingLine.antal +
          ' á ' +
          this.dkCurrencyPipe.transform(postingLine.stkPrisExclMoms) +
          ' = ' +
          this.dkCurrencyPipe.transform(postingLine.antal * postingLine.stkPrisExclMoms);
        if (postingLine.partnerUnitPriceNet !== null) {
          toolTekst +=
            '\r\n\r\nPartner = ' +
            this.dkCurrencyPipe.transform(postingLine.partnerUnitPriceNet) +
            ' (' +
            this.dkCurrencyPipe.transform(postingLine.antal * postingLine.partnerUnitPriceNet) +
            ')';
        }
        break;
      }
      case 'unitPriceGross': {
        toolTekst =
          postingLine.antal +
          ' á ' +
          this.dkCurrencyPipe.transform(postingLine.stkPrisInclMoms) +
          ' = ' +
          this.dkCurrencyPipe.transform(postingLine.antal * postingLine.stkPrisInclMoms);
        if (postingLine.partnerUnitPriceGross !== null) {
          toolTekst +=
            '\r\n\r\nPartner = ' +
            this.dkCurrencyPipe.transform(postingLine.partnerUnitPriceGross) +
            ' (' +
            this.dkCurrencyPipe.transform(postingLine.antal * postingLine.partnerUnitPriceGross) +
            ')';
        }
        break;
      }
      case 'totalPriceNet': {
        let total = 0;
        this._posteringsLinjer.forEach((x) => {
          if (x.partnerUnitPriceNet !== null) {
            total += x.partnerUnitPriceNet * x.antal;
          }
        });
        toolTekst = total !== 0 ? 'Partner = ' + this.dkCurrencyPipe.transform(total) : '';
        break;
      }
      case 'totalPriceGross': {
        let total = 0;
        this._posteringsLinjer.forEach((x) => {
          if (x.partnerUnitPriceGross !== null) {
            total += x.partnerUnitPriceGross * x.antal;
          }
        });
        toolTekst = total !== 0 ? 'Partner = ' + this.dkCurrencyPipe.transform(total) : '';
        break;
      }
    }
    return toolTekst;
  }
}
